/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeAvatar,
  DimeAvatarFallback,
  DimeAvatarImage,
  DimeButton,
  DimeDropdownMenu,
  DimeDropdownMenuCheckboxItem,
  DimeDropdownMenuContent,
  DimeDropdownMenuItem,
  DimeDropdownMenuLabel,
  DimeDropdownMenuRadioGroup,
  DimeDropdownMenuRadioItem,
  DimeDropdownMenuSeparator,
  DimeDropdownMenuShortcut,
  DimeDropdownMenuSub,
  DimeDropdownMenuSubContent,
  DimeDropdownMenuSubTrigger,
  DimeDropdownMenuTrigger,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import {
  BellIcon,
  Copy,
  Eye,
  FilePlus,
  FileText,
  MailIcon,
  MessageSquareIcon,
  Pencil,
  Settings,
  Trash2,
  User,
} from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/dropdown-menu')({
  component: DropdownMenuDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function BasicDropdown() {
  return (
    <DimeDropdownMenu>
      <DimeDropdownMenuTrigger asChild>
        <DimeButton variant="outline">Open menu</DimeButton>
      </DimeDropdownMenuTrigger>
      <DimeDropdownMenuContent>
        <DimeDropdownMenuItem>View details</DimeDropdownMenuItem>
        <DimeDropdownMenuItem>Edit</DimeDropdownMenuItem>
        <DimeDropdownMenuItem>Duplicate</DimeDropdownMenuItem>
      </DimeDropdownMenuContent>
    </DimeDropdownMenu>
  );
}

function SeparatorDropdown() {
  return (
    <DimeDropdownMenu>
      <DimeDropdownMenuTrigger asChild>
        <DimeButton variant="outline">With separator</DimeButton>
      </DimeDropdownMenuTrigger>
      <DimeDropdownMenuContent>
        <DimeDropdownMenuItem>View</DimeDropdownMenuItem>
        <DimeDropdownMenuItem>Edit</DimeDropdownMenuItem>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuItem>Export as CSV</DimeDropdownMenuItem>
        <DimeDropdownMenuItem>Export as PDF</DimeDropdownMenuItem>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuItem variant="destructive">Delete</DimeDropdownMenuItem>
      </DimeDropdownMenuContent>
    </DimeDropdownMenu>
  );
}

function CheckboxDropdown() {
  const [notifications, setNotifications] = React.useState({
    email: true,
    sms: false,
    push: true,
  });

  return (
    <DimeDropdownMenu>
      <DimeDropdownMenuTrigger asChild>
        <DimeButton variant="outline">Notifications</DimeButton>
      </DimeDropdownMenuTrigger>
      <DimeDropdownMenuContent className="w-52">
        <DimeDropdownMenuLabel>Notification Preferences</DimeDropdownMenuLabel>
        <DimeDropdownMenuCheckboxItem
          checked={notifications.email}
          onCheckedChange={(checked) =>
            setNotifications({ ...notifications, email: checked === true })
          }
          onSelect={(event) => event.preventDefault()}
        >
          <MailIcon />
          Email notifications
        </DimeDropdownMenuCheckboxItem>
        <DimeDropdownMenuCheckboxItem
          checked={notifications.sms}
          onCheckedChange={(checked) =>
            setNotifications({ ...notifications, sms: checked === true })
          }
          onSelect={(event) => event.preventDefault()}
        >
          <MessageSquareIcon />
          SMS notifications
        </DimeDropdownMenuCheckboxItem>
        <DimeDropdownMenuCheckboxItem
          checked={notifications.push}
          onCheckedChange={(checked) =>
            setNotifications({ ...notifications, push: checked === true })
          }
          onSelect={(event) => event.preventDefault()}
        >
          <BellIcon />
          Push notifications
        </DimeDropdownMenuCheckboxItem>
      </DimeDropdownMenuContent>
    </DimeDropdownMenu>
  );
}

function SubmenuDropdown() {
  return (
    <DimeDropdownMenu>
      <DimeDropdownMenuTrigger asChild>
        <DimeButton variant="outline">With submenu</DimeButton>
      </DimeDropdownMenuTrigger>
      <DimeDropdownMenuContent>
        <DimeDropdownMenuItem>
          <Eye />
          View
        </DimeDropdownMenuItem>
        <DimeDropdownMenuItem>
          <Pencil />
          Edit
        </DimeDropdownMenuItem>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuSub>
          <DimeDropdownMenuSubTrigger>
            <FilePlus />
            Export as
          </DimeDropdownMenuSubTrigger>
          <DimeDropdownMenuSubContent>
            <DimeDropdownMenuItem>
              <FileText />
              PDF
            </DimeDropdownMenuItem>
            <DimeDropdownMenuItem>
              <FileText />
              CSV
            </DimeDropdownMenuItem>
            <DimeDropdownMenuItem>
              <FileText />
              Excel
            </DimeDropdownMenuItem>
          </DimeDropdownMenuSubContent>
        </DimeDropdownMenuSub>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuItem variant="destructive">
          <Trash2 />
          Delete
        </DimeDropdownMenuItem>
      </DimeDropdownMenuContent>
    </DimeDropdownMenu>
  );
}

function ShortcutsDropdown() {
  return (
    <DimeDropdownMenu>
      <DimeDropdownMenuTrigger asChild>
        <DimeButton variant="outline">With shortcuts</DimeButton>
      </DimeDropdownMenuTrigger>
      <DimeDropdownMenuContent>
        <DimeDropdownMenuLabel>Actions</DimeDropdownMenuLabel>
        <DimeDropdownMenuItem>
          <Eye />
          View
          <DimeDropdownMenuShortcut>⌘↵</DimeDropdownMenuShortcut>
        </DimeDropdownMenuItem>
        <DimeDropdownMenuItem>
          <Pencil />
          Edit
          <DimeDropdownMenuShortcut>⌘E</DimeDropdownMenuShortcut>
        </DimeDropdownMenuItem>
        <DimeDropdownMenuItem>
          <Copy />
          Duplicate
          <DimeDropdownMenuShortcut>⌘D</DimeDropdownMenuShortcut>
        </DimeDropdownMenuItem>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuItem variant="destructive">
          <Trash2 />
          Delete
          <DimeDropdownMenuShortcut>⌫</DimeDropdownMenuShortcut>
        </DimeDropdownMenuItem>
      </DimeDropdownMenuContent>
    </DimeDropdownMenu>
  );
}

function RadioGroupDropdown() {
  const [status, setStatus] = React.useState('active');
  return (
    <DimeDropdownMenu>
      <DimeDropdownMenuTrigger asChild>
        <DimeButton variant="outline">Set status</DimeButton>
      </DimeDropdownMenuTrigger>
      <DimeDropdownMenuContent>
        <DimeDropdownMenuLabel>Status</DimeDropdownMenuLabel>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuRadioGroup value={status} onValueChange={setStatus}>
          <DimeDropdownMenuRadioItem value="active" onSelect={(ev) => ev.preventDefault()}>
            Active
          </DimeDropdownMenuRadioItem>
          <DimeDropdownMenuRadioItem value="inactive" onSelect={(ev) => ev.preventDefault()}>
            Inactive
          </DimeDropdownMenuRadioItem>
          <DimeDropdownMenuRadioItem value="archived" onSelect={(ev) => ev.preventDefault()}>
            Archived
          </DimeDropdownMenuRadioItem>
        </DimeDropdownMenuRadioGroup>
      </DimeDropdownMenuContent>
    </DimeDropdownMenu>
  );
}

function RadioIconsDropdown() {
  const [assignee, setAssignee] = React.useState('alice');
  const members = [
    { value: 'alice', label: 'Alice Smith', initials: 'AS' },
    { value: 'sarah', label: 'Sarah Lin', initials: 'SL' },
    { value: 'mike', label: 'Mike Chen', initials: 'MC' },
  ];
  return (
    <DimeDropdownMenu>
      <DimeDropdownMenuTrigger asChild>
        <DimeButton variant="outline">Assign to</DimeButton>
      </DimeDropdownMenuTrigger>
      <DimeDropdownMenuContent>
        <DimeDropdownMenuLabel>Team members</DimeDropdownMenuLabel>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuRadioGroup value={assignee} onValueChange={setAssignee}>
          {members.map((member) => (
            <DimeDropdownMenuRadioItem
              key={member.value}
              value={member.value}
              onSelect={(ev) => ev.preventDefault()}
            >
              <DimeAvatar className="size-5">
                <DimeAvatarFallback className="text-[9px]">{member.initials}</DimeAvatarFallback>
              </DimeAvatar>
              {member.label}
            </DimeDropdownMenuRadioItem>
          ))}
        </DimeDropdownMenuRadioGroup>
      </DimeDropdownMenuContent>
    </DimeDropdownMenu>
  );
}

function AvatarDropdown() {
  return (
    <DimeDropdownMenu>
      <DimeDropdownMenuTrigger asChild>
        <DimeButton variant="ghost" size="icon" aria-label="Account menu">
          <DimeAvatar className="size-7">
            <DimeAvatarImage src="" alt="Alice Smith" />
            <DimeAvatarFallback>JO</DimeAvatarFallback>
          </DimeAvatar>
        </DimeButton>
      </DimeDropdownMenuTrigger>
      <DimeDropdownMenuContent align="end">
        <DimeDropdownMenuLabel>name@example.com</DimeDropdownMenuLabel>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuItem>
          <User />
          Profile
        </DimeDropdownMenuItem>
        <DimeDropdownMenuItem>
          <Settings />
          Settings
        </DimeDropdownMenuItem>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuItem variant="destructive">Sign out</DimeDropdownMenuItem>
      </DimeDropdownMenuContent>
    </DimeDropdownMenu>
  );
}

function ComplexDropdown() {
  const [view, setView] = React.useState('grid');
  const [status, setStatus] = React.useState('active');

  return (
    <DimeDropdownMenu>
      <DimeDropdownMenuTrigger asChild>
        <DimeButton variant="outline">Complex menu</DimeButton>
      </DimeDropdownMenuTrigger>
      <DimeDropdownMenuContent className="w-52">
        <DimeDropdownMenuLabel>name@example.com</DimeDropdownMenuLabel>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuLabel>View</DimeDropdownMenuLabel>
        <DimeDropdownMenuRadioGroup value={view} onValueChange={setView}>
          <DimeDropdownMenuRadioItem value="grid" onSelect={(ev) => ev.preventDefault()}>
            Grid
          </DimeDropdownMenuRadioItem>
          <DimeDropdownMenuRadioItem value="list" onSelect={(ev) => ev.preventDefault()}>
            List
          </DimeDropdownMenuRadioItem>
        </DimeDropdownMenuRadioGroup>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuLabel>Status</DimeDropdownMenuLabel>
        <DimeDropdownMenuRadioGroup value={status} onValueChange={setStatus}>
          <DimeDropdownMenuRadioItem value="active" onSelect={(ev) => ev.preventDefault()}>
            Active
          </DimeDropdownMenuRadioItem>
          <DimeDropdownMenuRadioItem value="archived" onSelect={(ev) => ev.preventDefault()}>
            Archived
          </DimeDropdownMenuRadioItem>
        </DimeDropdownMenuRadioGroup>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuSub>
          <DimeDropdownMenuSubTrigger>
            <FilePlus />
            Export
          </DimeDropdownMenuSubTrigger>
          <DimeDropdownMenuSubContent>
            <DimeDropdownMenuItem>PDF</DimeDropdownMenuItem>
            <DimeDropdownMenuItem>CSV</DimeDropdownMenuItem>
          </DimeDropdownMenuSubContent>
        </DimeDropdownMenuSub>
        <DimeDropdownMenuSeparator />
        <DimeDropdownMenuItem>
          <Settings />
          Settings
          <DimeDropdownMenuShortcut>⌘,</DimeDropdownMenuShortcut>
        </DimeDropdownMenuItem>
        <DimeDropdownMenuItem variant="destructive">
          <Trash2 />
          Delete
          <DimeDropdownMenuShortcut>⌫</DimeDropdownMenuShortcut>
        </DimeDropdownMenuItem>
      </DimeDropdownMenuContent>
    </DimeDropdownMenu>
  );
}

function DropdownMenuDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Dropdown Menu</h1>
      <p className="mb-4 text-muted-foreground">
        A floating list of actions anchored to a trigger element. Compose with{' '}
        <code className="font-mono text-sm">DimeDropdownMenu</code> (Root),{' '}
        <code className="font-mono text-sm">DimeDropdownMenuTrigger</code>,{' '}
        <code className="font-mono text-sm">DimeDropdownMenuContent</code>, and item primitives.
      </p>
      <p className="mb-8 text-sm text-muted-foreground">
        <strong className="font-medium text-foreground">Checkbox items</strong> — use when
        selections are independent and multiple can be active at once (e.g. show/hide columns,
        filter by tags). <strong className="font-medium text-foreground">Radio group items</strong>{' '}
        — use when only one option can be active at a time (e.g. set status, assign to one person,
        choose view mode).
      </p>

      <DocsSection title="Basic dropdown">
        <Preview>
          <BasicDropdown />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap your trigger with <code className="font-mono">DimeDropdownMenuTrigger asChild</code>{' '}
          to forward click handling to any element.
        </p>
      </DocsSection>

      <DocsSection title="With icons">
        <Preview>
          <DimeDropdownMenu>
            <DimeDropdownMenuTrigger asChild>
              <DimeButton variant="outline">With icons</DimeButton>
            </DimeDropdownMenuTrigger>
            <DimeDropdownMenuContent>
              <DimeDropdownMenuItem>
                <Eye />
                View details
              </DimeDropdownMenuItem>
              <DimeDropdownMenuItem>
                <Pencil />
                Edit
              </DimeDropdownMenuItem>
              <DimeDropdownMenuItem>
                <Copy />
                Duplicate
              </DimeDropdownMenuItem>
              <DimeDropdownMenuSeparator />
              <DimeDropdownMenuItem variant="destructive">
                <Trash2 />
                Delete
              </DimeDropdownMenuItem>
            </DimeDropdownMenuContent>
          </DimeDropdownMenu>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Drop any lucide icon directly inside a{' '}
          <code className="font-mono">DimeDropdownMenuItem</code> — the component sizes and colours
          it automatically. No extra className needed.
        </p>
      </DocsSection>

      <DocsSection title="Separator and destructive item">
        <Preview>
          <SeparatorDropdown />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeDropdownMenuSeparator</code> to visually group related
          items. Pass <code className="font-mono">variant="destructive"</code> to{' '}
          <code className="font-mono">DimeDropdownMenuItem</code> for dangerous actions like Delete.
        </p>
      </DocsSection>

      <DocsSection title="Checkbox items">
        <Preview>
          <CheckboxDropdown />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeDropdownMenuCheckboxItem</code> renders a checkmark when{' '}
          <code className="font-mono">checked</code> is true. Pass{' '}
          <code className="font-mono">onSelect=(e) = e.preventDefault()</code> to keep the menu open
          after toggling.
        </p>
      </DocsSection>

      <DocsSection title="Submenu">
        <Preview>
          <SubmenuDropdown />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Nest <code className="font-mono">DimeDropdownMenuSub</code> inside content with a{' '}
          <code className="font-mono">DimeDropdownMenuSubTrigger</code> and{' '}
          <code className="font-mono">DimeDropdownMenuSubContent</code> for cascading menus.
        </p>
      </DocsSection>

      <DocsSection title="Shortcuts">
        <Preview>
          <ShortcutsDropdown />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeDropdownMenuLabel</code> to add a section heading and{' '}
          <code className="font-mono">DimeDropdownMenuShortcut</code> inside any item to show a
          keyboard hint aligned to the right.
        </p>
      </DocsSection>

      <DocsSection title="Radio group">
        <Preview>
          <RadioGroupDropdown />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap <code className="font-mono">DimeDropdownMenuRadioItem</code> in a{' '}
          <code className="font-mono">DimeDropdownMenuRadioGroup</code> with a controlled{' '}
          <code className="font-mono">value</code> to let users pick one option from a set.
        </p>
      </DocsSection>

      <DocsSection title="Radio with icons">
        <Preview>
          <RadioIconsDropdown />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Compose <code className="font-mono">DimeAvatar</code> inside{' '}
          <code className="font-mono">DimeDropdownMenuRadioItem</code> to show member avatars
          alongside radio options.
        </p>
      </DocsSection>

      <DocsSection title="Avatar trigger">
        <Preview>
          <AvatarDropdown />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeAvatar</code> as the trigger button contents for an
          account / profile menu. Pass <code className="font-mono">align="end"</code> to{' '}
          <code className="font-mono">DimeDropdownMenuContent</code> to anchor it to the right.
        </p>
      </DocsSection>

      <DocsSection title="Complex">
        <Preview>
          <ComplexDropdown />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Combine labels, radio groups, submenus, shortcuts, and destructive items for a
          fully-featured action panel.
        </p>
      </DocsSection>
    </div>
  );
}
